import { create } from 'zustand';
import { getAllSaleActivities } from '../api/activityApi';
import { fetchProductList as fetchProductListApi } from '../api/productApi';
import { extractCrossDayTimePatch } from '../utils/authResponse';
import { formatAndCategorizeProducts } from '../utils/productUtils';

/**
 * 全局状态管理 Store
 *
 * 使用 Zustand 进行状态管理，遵循以下设计原则：
 * 1. 单一职责：每个状态只负责一类数据
 * 2. 模块化：API 调用抽离到独立的 api 模块
 * 3. 数据转换：使用 utils/productUtils 处理数据格式化
 * 4. 持久化：用户数据自动保存到 localStorage
 *
 * 状态分类：
 * - 用户状态：user, isAuthenticated
 * - 商品状态：products, coinProducts, gemProducts, etc.
 * - UI 状态：各种 Modal 的开关状态
 * - 加载状态：apiLoading, loadingMessage
 */

// ============================================
// localStorage 工具函数
// ============================================

/**
 * 从 localStorage 恢复用户数据
 * @returns {Object|null} 用户数据或 null
 */
const getStoredUser = () => {
    try {
        const storedUser = localStorage.getItem('user');
        if (storedUser) {
            return JSON.parse(storedUser);
        }
    } catch (e) {
        console.error('Failed to parse stored user:', e);
    }
    return null;
};

/**
 * 保存用户数据到 localStorage
 * @param {Object|null} user - 用户数据
 */
const saveUserToStorage = (user) => {
    try {
        if (user) {
            localStorage.setItem('user', JSON.stringify(user));
        } else {
            localStorage.removeItem('user');
        }
    } catch (e) {
        console.error('Failed to save user to storage:', e);
    }
};

/**
 * 清除所有用户相关的存储数据
 */
const clearUserStorage = () => {
    try {
        localStorage.removeItem('emailCredentials');
        localStorage.removeItem('user');
    } catch (e) {
        console.error('Failed to clear user storage:', e);
    }
};

// 初始化时尝试恢复用户数据
const storedUser = getStoredUser();

// ============================================
// Store 定义
// ============================================

const useStore = create((set, get) => ({
    // -----------------------------------------
    // 用户状态
    // -----------------------------------------

    /**
     * 当前用户信息
     * 包含：id, playerid, token/Token, provider 等
     */
    user: storedUser,

    /**
     * 用户是否已认证
     * 根据 user 是否存在自动计算
     */
    isAuthenticated: !!storedUser,

    // -----------------------------------------
    // 商品状态
    // -----------------------------------------

    /** 所有商品的原始数组（格式化后） */
    products: [],

    /** 金币类商品数组 */
    coinProducts: [],

    /** 钻石类商品数组 */
    gemProducts: [],

    /** 道具类商品数组 */
    boostProducts: [],

    /** 金币基准商品数组（类型 1101，不参与活动折扣） */
    baseCoinProducts: [],

    /** 钻石基准商品数组（类型 1201，不参与活动折扣） */
    baseGemProducts: [],

    /** Hot Offer 商品数组 */
    hotOfferProducts: [],

    /** 商品数据是否已加载 */
    productsLoaded: false,

    /** 商品数据是否正在加载中 */
    productsLoading: false,

    /** 用户支付相关信息（如 PayPal 配置） */
    userPayInfo: null,

    // -----------------------------------------
    // 促销活动状态
    // -----------------------------------------

    /** 金币促销信息 { ratio, percentage, ... } 或 null */
    coinSaleActivity: null,

    /** 钻石促销信息 { ratio, percentage, ... } 或 null */
    gemSaleActivity: null,

    /** 促销活动是否已加载 */
    activitiesLoaded: false,

    /** 促销活动是否正在加载中（防重复请求） */
    activitiesLoading: false,

    // -----------------------------------------
    // 静态数据（Fallback）
    // -----------------------------------------

    /**
     * 静态商品数据
     * 当 API 调用失败时作为后备数据使用
     */
    staticProducts: [
        { id: 1100001, name: '1.99', description: 'Game Store 1.99 Coins', price: 1.99, paypal_price: 1.89, currency: 'USD', coins: 'x 2', rate: 0, image: 'assets/coin/coin3.webp' },
        { id: 1100003, name: '4.99', description: 'Game Store 4.99 Coins', price: 4.99, paypal_price: 4.74, currency: 'USD', coins: 'x 5', rate: 1.04, image: 'assets/coin/coin3.webp' },
        { id: 1100004, name: '9.99', description: 'Game Store 9.99 Coins', price: 9.99, paypal_price: 9.49, currency: 'USD', coins: 'x 10', rate: 1.12, image: 'assets/coin/coin3.webp' },
        { id: 1100006, name: '49.99', description: 'Game Store 49.99 Coins', price: 49.99, paypal_price: 47.49, currency: 'USD', coins: 'x 50', rate: 1.44, image: 'assets/coin/coin3.webp' },
        { id: 1100007, name: '99.99', description: 'Game Store 99.99 Coins', price: 99.99, paypal_price: 94.99, currency: 'USD', coins: 'x 100', rate: 1.6, image: 'assets/coin/coin3.webp' },
        { id: 1100009, name: '199.99', description: 'Game Store 199.99 Coins', price: 199.99, paypal_price: 189.99, currency: 'USD', coins: 'x 200', rate: 2.02, image: 'assets/coin/coin3.webp' },
        { id: 1100010, name: '299.99', description: 'Game Store 299.99 Coins', price: 299.99, paypal_price: 284.99, currency: 'USD', coins: 'x 300', rate: 2.32, image: 'assets/coin/coin3.webp' },
        { id: 1100012, name: '499.99', description: 'Game Store 499.99 Coins', price: 499.99, paypal_price: 474.99, currency: 'USD', coins: 'x 500', rate: 3.22, image: 'assets/coin/coin3.webp' },
    ],

    // -----------------------------------------
    // UI 状态 - 模态框
    // -----------------------------------------

    /** 登录模态框是否打开 */
    loginModalOpen: false,

    /** 支付模态框是否打开 */
    paymentModalOpen: false,

    /** 支付成功模态框是否打开 */
    paymentSuccessOpen: false,

    /** 刷新对话框是否打开（token 过期时显示） */
    refreshDialogOpen: false,

    /** 首屏自动登录检查是否完成（避免登录弹窗与自动登录竞态） */
    authCheckDone: false,

    // -----------------------------------------
    // 支付相关状态
    // -----------------------------------------

    /** 当前选中的商品（用于支付） */
    selectedProduct: null,

    /** 最后一次订单信息（用于支付成功显示） */
    lastOrder: null,

    // -----------------------------------------
    // 全局加载状态
    // -----------------------------------------

    /** 是否显示全局 loading */
    apiLoading: false,

    /** loading 提示信息 */
    loadingMessage: '',

    // ============================================
    // Actions - 用户相关
    // ============================================

    /**
     * 设置用户信息
     * 默认会持久化到 localStorage，可通过 options.persist 关闭持久化（仅当前页面会话生效）
     * @param {Object|null} user - 用户数据
     * @param {Object} [options]
     * @param {boolean} [options.persist=true] - 是否写入 localStorage
     */
    setUser: (user, options = {}) => {
        const { persist = true } = options;
        const prevUser = get().user;
        const prevGameToken = String(prevUser?.gameToken || '');
        const nextGameToken = String(user?.gameToken || '');

        // 识别“是否切换到了新用户会话”：
        // 只要 fpid / playerid / gameToken 任一变化，都视为新会话，需要重拉商店与促销数据
        const hasUserSwitched =
            !!user && (!prevUser || String(prevUser.id || '') !== String(user.id || '') || Number(prevUser.playerid || 0) !== Number(user.playerid || 0) || prevGameToken !== nextGameToken);

        if (persist) {
            saveUserToStorage(user);
        } else {
            // 非持久化登录：确保刷新后不会恢复登录状态
            localStorage.removeItem('user');
        }

        set({
            user,
            isAuthenticated: !!user,
            ...(hasUserSwitched
                ? {
                      // 强制清理缓存，触发 ProductList 的 useEffect 重新拉数据
                      products: [],
                      coinProducts: [],
                      baseCoinProducts: [],
                      gemProducts: [],
                      baseGemProducts: [],
                      boostProducts: [],
                      hotOfferProducts: [],
                      productsLoaded: false,
                      productsLoading: false,
                      userPayInfo: null,
                      coinSaleActivity: null,
                      gemSaleActivity: null,
                      activitiesLoaded: false,
                      activitiesLoading: false,
                  }
                : {}),
        });
    },

    /**
     * 用户登出
     * 清除用户数据、localStorage，如果是 Facebook 登录还会调用 FB.logout
     */
    logout: () => {
        const state = get();

        // 如果是 Facebook 登录，调用 FB.logout
        if (state.user?.provider === 'facebook' && window.FB) {
            window.FB.logout();
        }

        // 清除存储的用户数据
        clearUserStorage();

        // 重置用户状态
        set({
            user: null,
            isAuthenticated: false,
            // 登出后同步清空商品与促销缓存，避免后续账号串数据
            products: [],
            coinProducts: [],
            baseCoinProducts: [],
            gemProducts: [],
            baseGemProducts: [],
            boostProducts: [],
            hotOfferProducts: [],
            productsLoaded: false,
            productsLoading: false,
            userPayInfo: null,
            coinSaleActivity: null,
            gemSaleActivity: null,
            activitiesLoaded: false,
            activitiesLoading: false,
        });
    },

    // ============================================
    // Actions - UI 状态
    // ============================================

    /** 设置登录模态框开关 */
    setLoginModalOpen: (open) => set({ loginModalOpen: open }),

    /** 设置支付模态框开关 */
    setPaymentModalOpen: (open) => set({ paymentModalOpen: open }),

    /** 设置当前选中的商品 */
    setSelectedProduct: (product) => set({ selectedProduct: product }),

    /** 设置支付成功模态框开关 */
    setPaymentSuccessOpen: (open) => set({ paymentSuccessOpen: open }),

    /** 设置最后一次订单信息 */
    setLastOrder: (order) => set({ lastOrder: order }),

    /** 设置全局 loading 状态和提示信息 */
    setApiLoading: (loading, message = '') =>
        set({
            apiLoading: loading,
            loadingMessage: message,
        }),

    /** 设置刷新对话框开关 */
    setRefreshDialogOpen: (open) => set({ refreshDialogOpen: open }),

    /** 设置自动登录检查完成状态 */
    setAuthCheckDone: (done) => set({ authCheckDone: done }),

    // ============================================
    // Actions - 商品数据
    // ============================================

    /**
     * 获取商品列表
     * 从后端 API 获取商品数据，格式化后更新到状态中
     *
     * 前置条件：
     * - 用户必须已登录（isAuthenticated === true）
     * - 商品数据未在加载中
     * - 商品数据未已加载（可通过 resetProductsLoaded 重置）
     *
     * @param {number[]} [productTypes] - 可选，指定要获取的商品类型
     *   默认为 [1100, 1200, 1300]（金币、钻石、道具）
     */
    fetchProductList: async (productTypes) => {
        const state = get();

        // 防止重复加载
        if (state.productsLoading || state.productsLoaded) {
            return;
        }

        // 检查用户登录状态
        if (!state.isAuthenticated || !state.user) {
            console.error('[useStore] Cannot fetch products: user not authenticated');
            return;
        }

        // 设置加载状态
        set({ productsLoading: true });

        try {
            // 调用 API 获取商品数据
            const responseData = await fetchProductListApi(state.user, productTypes);
            const timePatch = extractCrossDayTimePatch(responseData);
            const hasTimePatch = Object.keys(timePatch).length > 0;
            // console.log('[countdown-debug][useStore][products-response-timePatch]', {
            //     hasTimePatch,
            //     timePatch,
            //     rawEndOfDay: responseData?.EndOfDay,
            //     rawNow: responseData?.Now,
            // });

            // 提取商品数组和支付信息
            const products = responseData.Products || [];
            const pay = responseData.Pay || {};

            // 使用工具函数格式化和分类商品数据
            const categorized = formatAndCategorizeProducts(products);

            // 更新状态
            set((currentState) => ({
                products: categorized.products,
                coinProducts: categorized.coinProducts,
                baseCoinProducts: categorized.baseCoinProducts,
                gemProducts: categorized.gemProducts,
                baseGemProducts: categorized.baseGemProducts,
                boostProducts: categorized.boostProducts,
                hotOfferProducts: categorized.hotOfferProducts,
                productsLoaded: true,
                productsLoading: false,
                userPayInfo: pay,
                ...(hasTimePatch && currentState.user ? { user: { ...currentState.user, ...timePatch } } : {}),
            }));
        } catch (error) {
            console.error('[useStore] Failed to fetch product list:', error);

            // API 失败时使用静态数据作为 fallback
            set((state) => ({
                products: state.staticProducts,
                coinProducts: state.staticProducts,
                gemProducts: [],
                boostProducts: [],
                hotOfferProducts: [],
                productsLoaded: true,
                productsLoading: false,
            }));
        }
    },

    /**
     * 重置商品加载状态
     * 用于需要重新加载商品数据的场景（如切换用户、刷新页面等）
     */
    resetProductsLoaded: () =>
        set({
            productsLoaded: false,
            products: [],
            coinProducts: [],
            baseCoinProducts: [],
            gemProducts: [],
            baseGemProducts: [],
            boostProducts: [],
            hotOfferProducts: [],
            userPayInfo: null,
        }),

    // ============================================
    // Actions - 促销活动
    // ============================================

    /**
     * 获取促销活动信息
     * 从后端 API 获取金币和钻石的促销活动
     */
    fetchSaleActivities: async () => {
        const state = get();
        if (!state.isAuthenticated || !state.user || state.activitiesLoading || state.activitiesLoaded) {
            return;
        }

        set({ activitiesLoading: true });

        try {
            const { coinSale, gemSale, timePatch = {} } = await getAllSaleActivities(state.user);
            const hasTimePatch = Object.keys(timePatch).length > 0;

            set((currentState) => ({
                coinSaleActivity: coinSale,
                gemSaleActivity: gemSale,
                activitiesLoaded: true,
                activitiesLoading: false,
                ...(hasTimePatch && currentState.user ? { user: { ...currentState.user, ...timePatch } } : {}),
            }));
        } catch (error) {
            console.error('[useStore] Failed to fetch sale activities:', error);
            set({ activitiesLoading: false });
        }
    },

    /**
     * 重置促销活动加载状态
     */
    resetActivitiesLoaded: () =>
        set({
            activitiesLoaded: false,
            activitiesLoading: false,
            coinSaleActivity: null,
            gemSaleActivity: null,
        }),
}));

export default useStore;
